import { useState, useCallback } from 'react';
import { phoneSystemAPI } from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';
import {
  ArrowLeft, ArrowRight, Plus, X, Loader2, Check, AlertCircle,
  Upload, FileText, Phone, Shield, MapPin, User, Mail, Building,
  RefreshCw, Clock, CheckCircle2, XCircle, Info,
} from 'lucide-react';

// ── Status badge colors ──
const statusColors = {
  draft: 'bg-slate-100 text-slate-700 border-slate-200',
  submitted: 'bg-blue-100 text-blue-700 border-blue-200',
  pending_review: 'bg-amber-100 text-amber-700 border-amber-200',
  waiting_for_signature: 'bg-purple-100 text-purple-700 border-purple-200',
  in_progress: 'bg-blue-100 text-blue-700 border-blue-200',
  docs_needed: 'bg-orange-100 text-orange-700 border-orange-200',
  approved: 'bg-emerald-100 text-emerald-700 border-emerald-200',
  scheduled: 'bg-cyan-100 text-cyan-700 border-cyan-200',
  completed: 'bg-green-100 text-green-700 border-green-200',
  rejected: 'bg-red-100 text-red-700 border-red-200',
  failed: 'bg-red-100 text-red-700 border-red-200',
  cancelled: 'bg-slate-100 text-slate-500 border-slate-200',
  pending: 'bg-yellow-100 text-yellow-700 border-yellow-200',
  in_review: 'bg-blue-100 text-blue-700 border-blue-200',
};

const statusLabels = {
  draft: 'Draft',
  submitted: 'Submitted',
  pending_review: 'Pending Review',
  waiting_for_signature: 'Awaiting LOA Signature',
  in_progress: 'In Progress',
  docs_needed: 'Action Required',
  approved: 'Approved',
  scheduled: 'Scheduled',
  completed: 'Completed',
  partially_completed: 'Partially Completed',
  rejected: 'Rejected',
  failed: 'Failed',
  cancelled: 'Cancelled',
  pending: 'Pending',
  in_review: 'In Review',
};

// ── Number input helper ──
function formatPhoneForDisplay(num) {
  const digits = num.replace(/\D/g, '');
  if (digits.length === 11 && digits.startsWith('1')) {
    return `+1 (${digits.slice(1, 4)}) ${digits.slice(4, 7)}-${digits.slice(7)}`;
  }
  if (digits.length === 10) {
    return `+1 (${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
  }
  return num;
}

function normalizeToE164(num) {
  const digits = num.replace(/\D/g, '');
  if (digits.length === 10) return `+1${digits}`;
  if (digits.length === 11 && digits.startsWith('1')) return `+${digits}`;
  return num.startsWith('+') ? num : `+${digits}`;
}


// ═══════════════════════════════════════════════════════════
// STEP 1: Enter Phone Numbers
// ═══════════════════════════════════════════════════════════
function StepNumbers({ data, onChange, onNext }) {
  const [input, setInput] = useState('');

  const addNumber = () => {
    const raw = input.trim();
    if (!raw) return;
    const e164 = normalizeToE164(raw);
    const digits = e164.replace(/\D/g, '');
    if (digits.length !== 11 || !digits.startsWith('1')) {
      toast.error('Enter a valid US phone number (10 digits)');
      return;
    }
    if (data.phone_numbers.includes(e164)) {
      toast.error('Number already added');
      return;
    }
    onChange({ phone_numbers: [...data.phone_numbers, e164] });
    setInput('');
  };

  const removeNumber = (num) => {
    onChange({ phone_numbers: data.phone_numbers.filter(n => n !== num) });
  };

  return (
    <div className="space-y-4" data-testid="step-numbers">
      <div>
        <h3 className="text-base font-semibold">Phone Numbers to Port</h3>
        <p className="text-sm text-muted-foreground mt-1">
          Enter the US phone numbers you want to transfer to Stock & Load.
          This supports US non-toll-free numbers only.
        </p>
      </div>

      <div className="bg-blue-50 border border-blue-200 rounded-md p-3" data-testid="port-scope-notice">
        <div className="flex gap-2">
          <Info className="w-4 h-4 text-blue-600 shrink-0 mt-0.5" />
          <p className="text-xs text-blue-800">
            This tool supports <strong>US local and mobile numbers</strong>.
            Toll-free numbers (800, 888, 877, etc.) and international numbers are not supported in this version.
          </p>
        </div>
      </div>

      <div className="flex gap-2">
        <Input
          value={input}
          onChange={e => setInput(e.target.value)}
          placeholder="(555) 123-4567"
          className="text-sm flex-1"
          data-testid="port-number-input"
          onKeyDown={e => e.key === 'Enter' && addNumber()}
        />
        <Button size="sm" onClick={addNumber} data-testid="add-number-btn">
          <Plus className="w-4 h-4 mr-1" /> Add
        </Button>
      </div>

      {data.phone_numbers.length > 0 && (
        <div className="space-y-1.5" data-testid="numbers-list">
          {data.phone_numbers.map(num => (
            <div key={num} className="flex items-center justify-between bg-muted/50 rounded-md px-3 py-2">
              <span className="text-sm font-mono">{formatPhoneForDisplay(num)}</span>
              <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => removeNumber(num)}>
                <X className="w-3.5 h-3.5 text-muted-foreground" />
              </Button>
            </div>
          ))}
          <p className="text-xs text-muted-foreground">{data.phone_numbers.length} number{data.phone_numbers.length > 1 ? 's' : ''} to port</p>
        </div>
      )}

      <div className="flex justify-end pt-2">
        <Button onClick={onNext} disabled={data.phone_numbers.length === 0} data-testid="next-step-btn">
          Check Portability <ArrowRight className="w-4 h-4 ml-1" />
        </Button>
      </div>
    </div>
  );
}


// ═══════════════════════════════════════════════════════════
// STEP 2: Portability Check Results
// ═══════════════════════════════════════════════════════════
function StepPortability({ data, onChange, onNext, onBack }) {
  const [checking, setChecking] = useState(false);
  const [results, setResults] = useState(data.portability_results || null);

  const runCheck = useCallback(async () => {
    setChecking(true);
    try {
      const res = await phoneSystemAPI.checkPortability({ phone_numbers: data.phone_numbers });
      const r = res.data.results || [];
      setResults(r);
      onChange({ portability_results: r });
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Portability check failed');
    } finally {
      setChecking(false);
    }
  }, [data.phone_numbers, onChange]);

  const portableCount = results ? results.filter(r => r.portable === true).length : 0;
  const allPortable = results && portableCount === results.length;

  return (
    <div className="space-y-4" data-testid="step-portability">
      <div>
        <h3 className="text-base font-semibold">Portability Check</h3>
        <p className="text-sm text-muted-foreground mt-1">
          We'll verify each number can be ported to our system before you fill out the transfer details.
        </p>
      </div>

      {!results && (
        <Button onClick={runCheck} disabled={checking} data-testid="run-check-btn">
          {checking ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Shield className="w-4 h-4 mr-1" />}
          {checking ? 'Checking...' : 'Run Portability Check'}
        </Button>
      )}

      {results && (
        <div className="space-y-2" data-testid="portability-results">
          {results.map(r => (
            <div key={r.phone_number} className={`flex items-center gap-3 rounded-md px-3 py-2.5 border ${r.portable ? 'bg-green-50 border-green-200' : r.portable === false ? 'bg-red-50 border-red-200' : 'bg-amber-50 border-amber-200'}`}>
              {r.portable ? (
                <CheckCircle2 className="w-4 h-4 text-green-600 shrink-0" />
              ) : r.portable === false ? (
                <XCircle className="w-4 h-4 text-red-600 shrink-0" />
              ) : (
                <AlertCircle className="w-4 h-4 text-amber-600 shrink-0" />
              )}
              <div className="min-w-0 flex-1">
                <span className="text-sm font-mono">{formatPhoneForDisplay(r.phone_number)}</span>
                {r.portable === false && r.not_portable_reason && (
                  <p className="text-xs text-red-600 mt-0.5">{r.not_portable_reason}</p>
                )}
                {r.error && (
                  <p className="text-xs text-amber-600 mt-0.5">Check failed: {r.error}</p>
                )}
                {r.pin_and_account_number_required && (
                  <p className="text-xs text-muted-foreground mt-0.5">Account number and PIN required</p>
                )}
              </div>
              <Badge className="text-[10px] shrink-0" variant="outline">
                {r.number_type || 'N/A'}
              </Badge>
            </div>
          ))}

          {portableCount > 0 && (
            <p className="text-xs text-green-700 font-medium">{portableCount} of {results.length} number{results.length > 1 ? 's' : ''} can be ported</p>
          )}
          {portableCount === 0 && results.length > 0 && (
            <div className="bg-red-50 border border-red-200 rounded-md p-3">
              <p className="text-xs text-red-700">None of these numbers can be ported. Please check the numbers and try again, or contact your current carrier for assistance.</p>
            </div>
          )}
        </div>
      )}

      <div className="flex justify-between pt-2">
        <Button variant="outline" onClick={onBack} data-testid="back-btn">
          <ArrowLeft className="w-4 h-4 mr-1" /> Back
        </Button>
        <Button onClick={onNext} disabled={!results || portableCount === 0} data-testid="next-step-btn">
          Continue <ArrowRight className="w-4 h-4 ml-1" />
        </Button>
      </div>
    </div>
  );
}


// ═══════════════════════════════════════════════════════════
// STEP 3: Account & Authorization Info
// ═══════════════════════════════════════════════════════════
function StepAccountInfo({ data, onChange, onNext, onBack }) {
  const update = (field, value) => onChange({ [field]: value });

  const canContinue = data.authorized_rep_name && data.authorized_rep_email && data.account_number && data.billing_name && data.billing_street && data.billing_city && data.billing_state && data.billing_zip;

  return (
    <div className="space-y-4" data-testid="step-account-info">
      <div>
        <h3 className="text-base font-semibold">Transfer Details</h3>
        <p className="text-sm text-muted-foreground mt-1">
          Provide information about your current carrier account. This must match the records your current carrier has on file.
        </p>
      </div>

      {/* Authorized Representative */}
      <div className="space-y-3 border rounded-md p-3">
        <div className="flex items-center gap-2 mb-1">
          <User className="w-4 h-4 text-blue-600" />
          <p className="text-sm font-medium">Authorized Representative</p>
        </div>
        <p className="text-xs text-muted-foreground -mt-1">
          This person must be authorized to approve the number transfer. They will receive an email to digitally sign the Letter of Authorization (LOA).
        </p>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div>
            <label className="text-xs font-medium text-muted-foreground block mb-1">Full Name *</label>
            <Input value={data.authorized_rep_name || ''} onChange={e => update('authorized_rep_name', e.target.value)} placeholder="John Smith" className="text-sm" data-testid="auth-rep-name" />
          </div>
          <div>
            <label className="text-xs font-medium text-muted-foreground block mb-1">Email Address *</label>
            <Input value={data.authorized_rep_email || ''} onChange={e => update('authorized_rep_email', e.target.value)} placeholder="john@company.com" type="email" className="text-sm" data-testid="auth-rep-email" />
          </div>
        </div>
      </div>

      {/* Carrier Account Info */}
      <div className="space-y-3 border rounded-md p-3">
        <div className="flex items-center gap-2 mb-1">
          <Building className="w-4 h-4 text-blue-600" />
          <p className="text-sm font-medium">Current Carrier Account</p>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div>
            <label className="text-xs font-medium text-muted-foreground block mb-1">Carrier Name</label>
            <Input value={data.losing_carrier_name || ''} onChange={e => update('losing_carrier_name', e.target.value)} placeholder="e.g. AT&T, Verizon" className="text-sm" data-testid="carrier-name" />
          </div>
          <div>
            <label className="text-xs font-medium text-muted-foreground block mb-1">Account Number *</label>
            <Input value={data.account_number || ''} onChange={e => update('account_number', e.target.value)} placeholder="Your carrier account #" className="text-sm" data-testid="account-number" />
          </div>
          <div>
            <label className="text-xs font-medium text-muted-foreground block mb-1">Account PIN (if required)</label>
            <Input value={data.account_pin || ''} onChange={e => update('account_pin', e.target.value)} placeholder="PIN or passcode" className="text-sm" data-testid="account-pin" />
          </div>
          <div>
            <label className="text-xs font-medium text-muted-foreground block mb-1">Primary Phone on Account</label>
            <Input value={data.account_telephone_number || ''} onChange={e => update('account_telephone_number', e.target.value)} placeholder="Main number on carrier account" className="text-sm" data-testid="account-phone" />
          </div>
        </div>
      </div>

      {/* Billing Address */}
      <div className="space-y-3 border rounded-md p-3">
        <div className="flex items-center gap-2 mb-1">
          <MapPin className="w-4 h-4 text-blue-600" />
          <p className="text-sm font-medium">Billing Address</p>
        </div>
        <p className="text-xs text-muted-foreground -mt-1">Must match the billing address on your current carrier account.</p>
        <div>
          <label className="text-xs font-medium text-muted-foreground block mb-1">Name on Account *</label>
          <Input value={data.billing_name || ''} onChange={e => update('billing_name', e.target.value)} placeholder="Business or person name" className="text-sm" data-testid="billing-name" />
        </div>
        <div>
          <label className="text-xs font-medium text-muted-foreground block mb-1">Street Address *</label>
          <Input value={data.billing_street || ''} onChange={e => update('billing_street', e.target.value)} placeholder="123 Main St, Suite 100" className="text-sm" data-testid="billing-street" />
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          <div className="col-span-2 sm:col-span-1">
            <label className="text-xs font-medium text-muted-foreground block mb-1">City *</label>
            <Input value={data.billing_city || ''} onChange={e => update('billing_city', e.target.value)} placeholder="City" className="text-sm" data-testid="billing-city" />
          </div>
          <div>
            <label className="text-xs font-medium text-muted-foreground block mb-1">State *</label>
            <Input value={data.billing_state || ''} onChange={e => update('billing_state', e.target.value)} placeholder="FL" maxLength={2} className="text-sm uppercase" data-testid="billing-state" />
          </div>
          <div>
            <label className="text-xs font-medium text-muted-foreground block mb-1">ZIP *</label>
            <Input value={data.billing_zip || ''} onChange={e => update('billing_zip', e.target.value)} placeholder="34231" className="text-sm" data-testid="billing-zip" />
          </div>
        </div>
      </div>

      <div className="flex justify-between pt-2">
        <Button variant="outline" onClick={onBack} data-testid="back-btn">
          <ArrowLeft className="w-4 h-4 mr-1" /> Back
        </Button>
        <Button onClick={onNext} disabled={!canContinue} data-testid="next-step-btn">
          Continue <ArrowRight className="w-4 h-4 ml-1" />
        </Button>
      </div>
    </div>
  );
}


// ═══════════════════════════════════════════════════════════
// STEP 4: Upload Documents (Utility Bill + Optional CSR)
// ═══════════════════════════════════════════════════════════
function StepUploadBill({ data, onChange, portRequestId, onNext, onBack }) {
  const [uploading, setUploading] = useState(false);
  const [fileName, setFileName] = useState(data.utility_bill_filename || null);
  const [csrUploading, setCsrUploading] = useState(false);
  const [csrFileName, setCsrFileName] = useState(data.csr_document_filename || null);

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const maxSize = 10 * 1024 * 1024;
    if (file.size > maxSize) {
      toast.error('File too large (max 10 MB)');
      return;
    }
    const allowed = ['application/pdf', 'image/jpeg', 'image/png', 'image/webp'];
    if (!allowed.includes(file.type)) {
      toast.error('Unsupported file type. Use PDF, JPEG, PNG, or WebP.');
      return;
    }
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      await phoneSystemAPI.uploadPortBill(portRequestId, formData);
      setFileName(file.name);
      onChange({ utility_bill_filename: file.name, has_utility_bill: true });
      toast.success('Utility bill uploaded');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Upload failed');
    } finally {
      setUploading(false);
    }
  };

  const handleCSRUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 10 * 1024 * 1024) { toast.error('File too large (max 10 MB)'); return; }
    const allowed = ['application/pdf', 'image/jpeg', 'image/png', 'image/webp'];
    if (!allowed.includes(file.type)) { toast.error('Unsupported file type. Use PDF, JPEG, PNG, or WebP.'); return; }
    setCsrUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      await phoneSystemAPI.uploadPortCSR(portRequestId, formData);
      setCsrFileName(file.name);
      onChange({ csr_document_filename: file.name, has_csr_document: true });
      toast.success('CSR uploaded');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Upload failed');
    } finally {
      setCsrUploading(false);
    }
  };

  return (
    <div className="space-y-4" data-testid="step-upload-bill">
      <div>
        <h3 className="text-base font-semibold">Supporting Documents</h3>
        <p className="text-sm text-muted-foreground mt-1">
          Upload documents to support your port request. A utility bill helps verify ownership. A CSR can help if account details don't match.
        </p>
      </div>

      {/* Utility Bill */}
      <div className="space-y-2">
        <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider">Utility Bill / Carrier Invoice</p>
        <div className="border-2 border-dashed rounded-lg p-5 text-center">
          {fileName ? (
            <div className="space-y-2">
              <FileText className="w-7 h-7 mx-auto text-green-600" />
              <p className="text-sm font-medium text-green-700">{fileName}</p>
              <p className="text-xs text-muted-foreground">File uploaded successfully</p>
              <label className="cursor-pointer">
                <span className="text-xs text-blue-600 hover:underline">Replace file</span>
                <input type="file" className="hidden" accept=".pdf,.jpg,.jpeg,.png,.webp" onChange={handleUpload} />
              </label>
            </div>
          ) : (
            <label className="cursor-pointer block" data-testid="upload-area">
              {uploading ? (
                <Loader2 className="w-7 h-7 mx-auto text-blue-600 animate-spin" />
              ) : (
                <Upload className="w-7 h-7 mx-auto text-muted-foreground" />
              )}
              <p className="text-sm font-medium mt-2">{uploading ? 'Uploading...' : 'Click to upload'}</p>
              <p className="text-xs text-muted-foreground mt-1">PDF, JPEG, PNG, or WebP (max 10 MB)</p>
              <input type="file" className="hidden" accept=".pdf,.jpg,.jpeg,.png,.webp" onChange={handleUpload} disabled={uploading} />
            </label>
          )}
        </div>
      </div>

      {/* CSR Document */}
      <div className="space-y-2">
        <div className="flex items-center gap-2">
          <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider">Customer Service Record (CSR)</p>
          <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-muted text-muted-foreground font-medium">Optional</span>
        </div>
        <p className="text-xs text-muted-foreground">
          A CSR from your current carrier can help prevent port rejections when account details or billing info don't exactly match carrier records.
          You can request a CSR by calling your current provider.
        </p>
        <div className="border-2 border-dashed rounded-lg p-5 text-center" data-testid="csr-upload-area">
          {csrFileName ? (
            <div className="space-y-2">
              <FileText className="w-7 h-7 mx-auto text-green-600" />
              <p className="text-sm font-medium text-green-700">{csrFileName}</p>
              <p className="text-xs text-muted-foreground">CSR uploaded</p>
              <label className="cursor-pointer">
                <span className="text-xs text-blue-600 hover:underline">Replace file</span>
                <input type="file" className="hidden" accept=".pdf,.jpg,.jpeg,.png,.webp" onChange={handleCSRUpload} />
              </label>
            </div>
          ) : (
            <label className="cursor-pointer block" data-testid="csr-upload-trigger">
              {csrUploading ? (
                <Loader2 className="w-7 h-7 mx-auto text-blue-600 animate-spin" />
              ) : (
                <Upload className="w-7 h-7 mx-auto text-muted-foreground/50" />
              )}
              <p className="text-sm font-medium mt-2 text-muted-foreground">{csrUploading ? 'Uploading...' : 'Click to upload CSR'}</p>
              <p className="text-xs text-muted-foreground mt-1">PDF, JPEG, PNG, or WebP (max 10 MB)</p>
              <input type="file" className="hidden" accept=".pdf,.jpg,.jpeg,.png,.webp" onChange={handleCSRUpload} disabled={csrUploading} />
            </label>
          )}
        </div>
      </div>

      <div className="flex justify-between pt-2">
        <Button variant="outline" onClick={onBack} data-testid="back-btn">
          <ArrowLeft className="w-4 h-4 mr-1" /> Back
        </Button>
        <Button onClick={onNext} data-testid="next-step-btn">
          Review Request <ArrowRight className="w-4 h-4 ml-1" />
        </Button>
      </div>
    </div>
  );
}


// ═══════════════════════════════════════════════════════════
// STEP 5: Review & Submit
// ═══════════════════════════════════════════════════════════
function StepReview({ data, onBack, onSubmit, submitting }) {
  return (
    <div className="space-y-4" data-testid="step-review">
      <div>
        <h3 className="text-base font-semibold">Review & Submit</h3>
        <p className="text-sm text-muted-foreground mt-1">
          Review the information below before submitting. Once submitted, the porting process begins and typically takes 5-15 business days.
        </p>
      </div>

      {/* Numbers */}
      <div className="border rounded-md p-3 space-y-1">
        <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider">Numbers to Port</p>
        <div className="flex flex-wrap gap-1.5">
          {(data.phone_numbers || []).map(n => (
            <Badge key={n} variant="outline" className="font-mono text-xs">{formatPhoneForDisplay(n)}</Badge>
          ))}
        </div>
      </div>

      {/* Account Info */}
      <div className="border rounded-md p-3 space-y-2">
        <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider">Transfer Details</p>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-1 text-sm">
          <div><span className="text-muted-foreground">Auth Rep:</span> {data.authorized_rep_name}</div>
          <div><span className="text-muted-foreground">Email:</span> {data.authorized_rep_email}</div>
          {data.losing_carrier_name && <div><span className="text-muted-foreground">Carrier:</span> {data.losing_carrier_name}</div>}
          <div><span className="text-muted-foreground">Account #:</span> {data.account_number}</div>
          {data.account_pin && <div><span className="text-muted-foreground">PIN:</span> ****</div>}
        </div>
      </div>

      {/* Billing Address */}
      <div className="border rounded-md p-3 space-y-1">
        <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider">Billing Address</p>
        <p className="text-sm">{data.billing_name}</p>
        <p className="text-sm text-muted-foreground">{data.billing_street}</p>
        <p className="text-sm text-muted-foreground">{data.billing_city}, {data.billing_state} {data.billing_zip}</p>
      </div>

      {/* Documents */}
      <div className="border rounded-md p-3 space-y-2">
        <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider">Documents</p>
        <div className="space-y-1.5">
          <div className="flex items-center gap-2">
            <span className="text-xs text-muted-foreground w-20 shrink-0">Utility Bill:</span>
            {data.utility_bill_filename ? (
              <div className="flex items-center gap-1.5">
                <FileText className="w-3.5 h-3.5 text-green-600" />
                <span className="text-sm">{data.utility_bill_filename}</span>
              </div>
            ) : (
              <span className="text-sm text-muted-foreground">Not uploaded (optional but recommended)</span>
            )}
          </div>
          <div className="flex items-center gap-2">
            <span className="text-xs text-muted-foreground w-20 shrink-0">CSR:</span>
            {data.csr_document_filename ? (
              <div className="flex items-center gap-1.5">
                <FileText className="w-3.5 h-3.5 text-green-600" />
                <span className="text-sm">{data.csr_document_filename}</span>
              </div>
            ) : (
              <span className="text-sm text-muted-foreground">Not uploaded (optional)</span>
            )}
          </div>
        </div>
      </div>

      {/* What happens next */}
      <div className="bg-blue-50 border border-blue-200 rounded-md p-3">
        <p className="text-xs text-blue-800 font-medium mb-1">What happens after you submit:</p>
        <ol className="text-xs text-blue-700 space-y-0.5 list-decimal list-inside">
          <li>Your request is submitted to the porting provider</li>
          <li>A Letter of Authorization (LOA) is emailed to <strong>{data.authorized_rep_email}</strong> for digital signature</li>
          <li>Once signed, the request goes to your current carrier for processing</li>
          <li>When complete, the numbers are automatically added to your phone system</li>
        </ol>
        <p className="text-xs text-blue-700 mt-1.5">Typical timeline: <strong>5-15 business days</strong> (varies by carrier)</p>
      </div>

      <div className="flex justify-between pt-2">
        <Button variant="outline" onClick={onBack} data-testid="back-btn">
          <ArrowLeft className="w-4 h-4 mr-1" /> Back
        </Button>
        <Button onClick={onSubmit} disabled={submitting} data-testid="submit-port-btn">
          {submitting ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Check className="w-4 h-4 mr-1" />}
          {submitting ? 'Submitting...' : 'Submit Port Request'}
        </Button>
      </div>
    </div>
  );
}


// ═══════════════════════════════════════════════════════════
// PORT REQUEST DETAIL / STATUS VIEW
// ═══════════════════════════════════════════════════════════
export function PortRequestDetail({ request: pr, onRefresh, onBack }) {
  const [syncing, setSyncing] = useState(false);
  const [csrUploading, setCsrUploading] = useState(false);
  const [csrRemoving, setCsrRemoving] = useState(false);

  const handleSync = async () => {
    setSyncing(true);
    try {
      await phoneSystemAPI.syncPortRequest(pr.id);
      toast.success('Status refreshed');
      onRefresh?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Sync failed');
    } finally {
      setSyncing(false);
    }
  };

  const csrLocked = ['completed', 'cancelled'].includes(pr.status);

  const handleCSRUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 10 * 1024 * 1024) { toast.error('File too large (max 10 MB)'); return; }
    const allowed = ['application/pdf', 'image/jpeg', 'image/png', 'image/webp'];
    if (!allowed.includes(file.type)) { toast.error('Unsupported file type. Use PDF, JPEG, PNG, or WebP.'); return; }
    setCsrUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      await phoneSystemAPI.uploadPortCSR(pr.id, formData);
      toast.success('CSR uploaded');
      onRefresh?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Upload failed');
    } finally {
      setCsrUploading(false);
    }
  };

  const handleCSRRemove = async () => {
    setCsrRemoving(true);
    try {
      await phoneSystemAPI.removePortCSR(pr.id);
      toast.success('CSR removed');
      onRefresh?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Remove failed');
    } finally {
      setCsrRemoving(false);
    }
  };

  const numbers = pr.phone_numbers || (pr.phone_number ? [pr.phone_number] : []);
  const timeline = pr.status_timeline || [];
  const canSync = pr.twilio_port_in_sid && !['draft', 'completed', 'cancelled'].includes(pr.status);

  return (
    <div className="space-y-4" data-testid="port-request-detail">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div className="flex items-center gap-2">
          {onBack && (
            <Button variant="ghost" size="icon" className="h-8 w-8" onClick={onBack}>
              <ArrowLeft className="w-4 h-4" />
            </Button>
          )}
          <h3 className="text-base font-semibold">Port Request</h3>
          <Badge className={`${statusColors[pr.status] || ''} text-xs`}>{statusLabels[pr.status] || pr.status}</Badge>
        </div>
        {canSync && (
          <Button size="sm" variant="outline" onClick={handleSync} disabled={syncing} data-testid="sync-btn">
            <RefreshCw className={`w-3.5 h-3.5 mr-1 ${syncing ? 'animate-spin' : ''}`} /> Refresh Status
          </Button>
        )}
      </div>

      {/* Error / Rejection */}
      {(pr.error_message || pr.rejection_detail) && (
        <div className="bg-red-50 border border-red-200 rounded-md p-3" data-testid="port-error">
          <div className="flex gap-2">
            <AlertCircle className="w-4 h-4 text-red-600 shrink-0 mt-0.5" />
            <div>
              {pr.error_message && <p className="text-xs text-red-700">{pr.error_message}</p>}
              {pr.rejection_detail && <p className="text-xs text-red-600 mt-1">{pr.rejection_detail}</p>}
            </div>
          </div>
        </div>
      )}

      {/* Status-specific guidance */}
      {pr.status === 'waiting_for_signature' && (
        <div className="bg-purple-50 border border-purple-200 rounded-md p-3">
          <div className="flex gap-2">
            <Mail className="w-4 h-4 text-purple-600 shrink-0 mt-0.5" />
            <p className="text-xs text-purple-700">
              A Letter of Authorization has been emailed to <strong>{pr.authorized_rep_email}</strong>.
              The authorized representative must sign it digitally to proceed. Check spam/junk if not received.
            </p>
          </div>
        </div>
      )}

      {pr.status === 'docs_needed' && (
        <div className="bg-orange-50 border border-orange-200 rounded-md p-3">
          <div className="flex gap-2">
            <AlertCircle className="w-4 h-4 text-orange-600 shrink-0 mt-0.5" />
            <div>
              <p className="text-xs text-orange-700">
                Action required — the porting provider needs additional information or corrections.
                Check the timeline below for details.
              </p>
              {!pr.has_csr_document && (
                <p className="text-xs text-orange-600 mt-1.5 font-medium">
                  Tip: Uploading a Customer Service Record (CSR) from your current carrier may help resolve account verification issues.
                </p>
              )}
            </div>
          </div>
        </div>
      )}

      {pr.status === 'rejected' && !pr.has_csr_document && (
        <div className="bg-amber-50 border border-amber-200 rounded-md p-3">
          <div className="flex gap-2">
            <AlertCircle className="w-4 h-4 text-amber-600 shrink-0 mt-0.5" />
            <p className="text-xs text-amber-700">
              This request was rejected. If the rejection was due to account details not matching,
              attaching a CSR from your current carrier may help resolve the mismatch on a new attempt.
            </p>
          </div>
        </div>
      )}

      {pr.status === 'completed' && (
        <div className="bg-green-50 border border-green-200 rounded-md p-3">
          <div className="flex gap-2">
            <CheckCircle2 className="w-4 h-4 text-green-600 shrink-0 mt-0.5" />
            <p className="text-xs text-green-700">
              Port complete! These numbers have been added to your phone system and are ready to use.
            </p>
          </div>
        </div>
      )}

      {/* Numbers */}
      <div className="border rounded-md p-3 space-y-1.5">
        <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider">Phone Numbers ({numbers.length})</p>
        <div className="flex flex-wrap gap-1.5">
          {numbers.map(n => (
            <Badge key={n} variant="outline" className="font-mono text-xs">{formatPhoneForDisplay(n)}</Badge>
          ))}
        </div>
      </div>

      {/* Details summary */}
      <div className="border rounded-md p-3">
        <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider mb-2">Details</p>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-1.5 text-sm">
          {pr.losing_carrier_name && <div><span className="text-muted-foreground text-xs">Carrier:</span> <span className="text-xs">{pr.losing_carrier_name}</span></div>}
          {pr.authorized_rep_name && <div><span className="text-muted-foreground text-xs">Auth Rep:</span> <span className="text-xs">{pr.authorized_rep_name}</span></div>}
          {pr.billing_name && <div><span className="text-muted-foreground text-xs">Billing:</span> <span className="text-xs">{pr.billing_name}</span></div>}
          {pr.twilio_port_in_sid && <div className="col-span-full"><span className="text-muted-foreground text-xs">Twilio SID:</span> <span className="text-xs font-mono">{pr.twilio_port_in_sid}</span></div>}
        </div>
      </div>

      {/* Documents */}
      <div className="border rounded-md p-3 space-y-3" data-testid="port-documents">
        <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider">Documents</p>
        <div className="space-y-2">
          {/* Utility Bill */}
          <div className="flex items-center gap-2 text-sm">
            <span className="text-xs text-muted-foreground w-20 shrink-0">Utility Bill:</span>
            {pr.has_utility_bill ? (
              <div className="flex items-center gap-1.5 min-w-0">
                <FileText className="w-3.5 h-3.5 text-green-600 shrink-0" />
                <span className="text-xs truncate">{pr.utility_bill_filename}</span>
              </div>
            ) : (
              <span className="text-xs text-muted-foreground">Not uploaded</span>
            )}
          </div>

          {/* CSR */}
          <div className="flex items-start gap-2 text-sm">
            <span className="text-xs text-muted-foreground w-20 shrink-0 mt-0.5">CSR:</span>
            {pr.has_csr_document ? (
              <div className="flex items-center gap-2 min-w-0 flex-wrap">
                <div className="flex items-center gap-1.5 min-w-0">
                  <FileText className="w-3.5 h-3.5 text-green-600 shrink-0" />
                  <span className="text-xs truncate">{pr.csr_document_filename}</span>
                </div>
                {pr.csr_uploaded_at && (
                  <span className="text-[10px] text-muted-foreground">
                    {new Date(pr.csr_uploaded_at).toLocaleDateString()}
                  </span>
                )}
                {!csrLocked && (
                  <div className="flex gap-1.5">
                    <label className="cursor-pointer text-[10px] text-blue-600 hover:underline" data-testid="csr-replace-btn">
                      Replace
                      <input type="file" className="hidden" accept=".pdf,.jpg,.jpeg,.png,.webp" onChange={handleCSRUpload} disabled={csrUploading} />
                    </label>
                    <button
                      onClick={handleCSRRemove}
                      disabled={csrRemoving}
                      className="text-[10px] text-red-600 hover:underline disabled:opacity-50"
                      data-testid="csr-remove-btn"
                    >
                      {csrRemoving ? 'Removing...' : 'Remove'}
                    </button>
                  </div>
                )}
              </div>
            ) : csrLocked ? (
              <span className="text-xs text-muted-foreground">Not attached</span>
            ) : (
              <label className="cursor-pointer flex items-center gap-1.5 text-xs text-blue-600 hover:underline" data-testid="csr-detail-upload-btn">
                {csrUploading ? (
                  <><Loader2 className="w-3 h-3 animate-spin" /> Uploading...</>
                ) : (
                  <><Upload className="w-3 h-3" /> Upload CSR</>
                )}
                <input type="file" className="hidden" accept=".pdf,.jpg,.jpeg,.png,.webp" onChange={handleCSRUpload} disabled={csrUploading} />
              </label>
            )}
          </div>
        </div>
        {!csrLocked && !pr.has_csr_document && (
          <p className="text-[10px] text-muted-foreground leading-relaxed">
            A Customer Service Record (CSR) from your current carrier can help prevent rejections when account details don't exactly match. Contact your carrier to request one.
          </p>
        )}
      </div>

      {/* Timeline */}
      {timeline.length > 0 && (
        <div className="border rounded-md p-3" data-testid="port-timeline">
          <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider mb-2">Timeline</p>
          <div className="space-y-2">
            {timeline.map((entry, i) => (
              <div key={i} className="flex gap-2">
                <div className="flex flex-col items-center">
                  <div className={`w-2 h-2 rounded-full mt-1.5 ${i === timeline.length - 1 ? 'bg-blue-500' : 'bg-muted-foreground/30'}`} />
                  {i < timeline.length - 1 && <div className="w-px flex-1 bg-muted-foreground/20 mt-1" />}
                </div>
                <div className="pb-2 min-w-0 flex-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <Badge className={`${statusColors[entry.status] || ''} text-[10px]`}>{statusLabels[entry.status] || entry.status}</Badge>
                    <span className="text-[10px] text-muted-foreground">{new Date(entry.timestamp).toLocaleString()}</span>
                  </div>
                  {entry.detail && <p className="text-xs text-muted-foreground mt-0.5">{entry.detail}</p>}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}


// ═══════════════════════════════════════════════════════════
// MAIN WIZARD EXPORT
// ═══════════════════════════════════════════════════════════
export function PortInWizard({ onClose, onCreated }) {
  const [step, setStep] = useState(1);
  const [submitting, setSubmitting] = useState(false);
  const [portRequestId, setPortRequestId] = useState(null);
  const [data, setData] = useState({
    phone_numbers: [],
    portability_results: null,
    authorized_rep_name: '',
    authorized_rep_email: '',
    losing_carrier_name: '',
    account_number: '',
    account_pin: '',
    account_telephone_number: '',
    billing_name: '',
    billing_street: '',
    billing_city: '',
    billing_state: '',
    billing_zip: '',
    billing_country: 'US',
    utility_bill_filename: null,
    has_utility_bill: false,
    csr_document_filename: null,
    has_csr_document: false,
    notes: '',
  });

  const updateData = (changes) => setData(prev => ({ ...prev, ...changes }));

  // Save draft when moving past step 2 (portability check)
  const saveDraft = useCallback(async () => {
    if (portRequestId) {
      try {
        await phoneSystemAPI.updatePortRequest(portRequestId, {
          phone_numbers: data.phone_numbers,
          losing_carrier_name: data.losing_carrier_name,
          account_number: data.account_number,
          account_pin: data.account_pin,
          account_telephone_number: data.account_telephone_number,
          authorized_rep_name: data.authorized_rep_name,
          authorized_rep_email: data.authorized_rep_email,
          billing_name: data.billing_name,
          billing_street: data.billing_street,
          billing_city: data.billing_city,
          billing_state: data.billing_state,
          billing_zip: data.billing_zip,
          billing_country: data.billing_country,
        });
      } catch { /* silent save */ }
      return portRequestId;
    }
    // Create draft
    try {
      const res = await phoneSystemAPI.createPortRequest({
        phone_numbers: data.phone_numbers,
        losing_carrier_name: data.losing_carrier_name,
      });
      const id = res.data.id;
      setPortRequestId(id);
      return id;
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to create port request');
      return null;
    }
  }, [portRequestId, data]);

  const handleSubmit = async () => {
    setSubmitting(true);
    try {
      // Save latest data
      const id = await saveDraft();
      if (!id) { setSubmitting(false); return; }
      // Update with all fields before submit
      await phoneSystemAPI.updatePortRequest(id, {
        phone_numbers: data.phone_numbers,
        losing_carrier_name: data.losing_carrier_name,
        account_number: data.account_number,
        account_pin: data.account_pin,
        account_telephone_number: data.account_telephone_number || data.phone_numbers[0],
        authorized_rep_name: data.authorized_rep_name,
        authorized_rep_email: data.authorized_rep_email,
        billing_name: data.billing_name,
        billing_street: data.billing_street,
        billing_city: data.billing_city,
        billing_state: data.billing_state,
        billing_zip: data.billing_zip,
        billing_country: data.billing_country,
      });
      // Submit to Twilio
      const res = await phoneSystemAPI.submitPortRequest(id);
      if (res.data.status === 'failed') {
        toast.error(`Submission failed: ${res.data.error_message || 'Unknown error'}`);
      } else {
        toast.success('Port request submitted successfully');
        onCreated?.();
        onClose?.();
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Submission failed');
    } finally {
      setSubmitting(false);
    }
  };

  const goToStep = async (target) => {
    // Save draft when leaving step 2 forward
    if (step === 2 && target > 2) {
      await saveDraft();
    }
    // Save when leaving step 3
    if (step === 3 && target > 3 && portRequestId) {
      await saveDraft();
    }
    setStep(target);
  };

  // Progress indicator
  const steps = [
    { num: 1, label: 'Numbers' },
    { num: 2, label: 'Portability' },
    { num: 3, label: 'Details' },
    { num: 4, label: 'Document' },
    { num: 5, label: 'Review' },
  ];

  return (
    <Card data-testid="port-in-wizard">
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between gap-2">
          <div className="flex items-center gap-2">
            <Phone className="w-5 h-5 text-green-600" />
            <CardTitle className="text-base">Port Existing Numbers</CardTitle>
          </div>
          {onClose && (
            <Button variant="ghost" size="icon" className="h-8 w-8" onClick={onClose}>
              <X className="w-4 h-4" />
            </Button>
          )}
        </div>
        <CardDescription className="text-xs">
          Transfer your existing phone numbers from another carrier to Stock & Load.
        </CardDescription>
      </CardHeader>

      <CardContent>
        {/* Step progress */}
        <div className="flex items-center gap-1 mb-5 overflow-x-auto pb-1" data-testid="wizard-progress">
          {steps.map((s, i) => (
            <div key={s.num} className="flex items-center">
              <div className={`flex items-center gap-1.5 px-2 py-1 rounded-full text-[11px] font-medium whitespace-nowrap ${step === s.num ? 'bg-blue-100 text-blue-700' : step > s.num ? 'bg-green-100 text-green-700' : 'bg-muted text-muted-foreground'}`}>
                {step > s.num ? <Check className="w-3 h-3" /> : <span>{s.num}</span>}
                <span className="hidden sm:inline">{s.label}</span>
              </div>
              {i < steps.length - 1 && <div className="w-4 sm:w-6 h-px bg-muted-foreground/20 mx-0.5" />}
            </div>
          ))}
        </div>

        {/* Step content */}
        {step === 1 && <StepNumbers data={data} onChange={updateData} onNext={() => goToStep(2)} />}
        {step === 2 && <StepPortability data={data} onChange={updateData} onNext={() => goToStep(3)} onBack={() => goToStep(1)} />}
        {step === 3 && <StepAccountInfo data={data} onChange={updateData} onNext={() => goToStep(4)} onBack={() => goToStep(2)} />}
        {step === 4 && <StepUploadBill data={data} onChange={updateData} portRequestId={portRequestId} onNext={() => goToStep(5)} onBack={() => goToStep(3)} />}
        {step === 5 && <StepReview data={data} onBack={() => goToStep(4)} onSubmit={handleSubmit} submitting={submitting} />}
      </CardContent>
    </Card>
  );
}

export default PortInWizard;
